'use client';
import React, { useState } from 'react';
import Link from 'next/link';

const PhoneIcon = ({ size = 14 }: {size?: number;}) =>
<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1 19.79 19.79 0 0 1 1.61 4.54 2 2 0 0 1 3.59 2.36h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.91-1.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>;


const CheckIcon = ({ size = 16 }: {size?: number;}) =>
<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>;


const ArrowIcon = ({ size = 14 }: {size?: number;}) =>
<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>;


interface FormData {
  first_name: string;
  last_name: string;
  firm_name: string;
  website: string;
  industry: string;
  monthly_volume: string;
  phone: string;
  email: string;
  notes: string;
}

export default function ApplyPage() {
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    firm_name: '',
    website: '',
    industry: '',
    monthly_volume: '',
    phone: '',
    email: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const isValid =
  formData.first_name.trim() !== '' &&
  formData.last_name.trim() !== '' &&
  formData.firm_name.trim() !== '' &&
  formData.industry !== '' &&
  formData.monthly_volume !== '' &&
  formData.phone.trim() !== '' &&
  formData.email.trim() !== '';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSubmitting(true);
    setError('');

    const SCHEDULING_URL = 'https://app.apollo.io/#/meet/3oy-n73-27z/30-min';

    try {
      const payload = {
        ...formData,
        source_page: 'cpa',
        submission_timestamp: new Date().toISOString()
      };

      // Fire-and-forget: Apollo contacts API
      fetch('https://api.apollo.io/v1/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_key: '3un1AiQEHHPJHCWQ4xNYPw',
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone_number: formData.phone,
          organization_name: formData.firm_name,
          title: 'CPA',
          contact_stage_id: '69b85ebb15379d0011ebcbe6'
        })
      }).catch(() => {});

      // Submit to backend (non-blocking)
      fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => {});

      // Redirect after short delay to allow requests to fire
      setTimeout(() => {
        window.location.href = SCHEDULING_URL;
      }, 800);

    } catch (err) {
      console.error('Submission error:', err);
      window.location.href = SCHEDULING_URL;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ===== APPLY HERO ===== */}
      <section
        className="relative overflow-hidden noise-overlay"
        style={{ background: 'linear-gradient(135deg, #041f4d 0%, #0A3D91 55%, #2F6BFF 100%)' }}
        aria-label="Apply hero">
        
        {/* Grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}
          aria-hidden="true" />
        
        <div
          className="absolute top-0 right-1/4 w-96 h-96 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(47,107,255,0.2) 0%, transparent 70%)' }}
          aria-hidden="true" />
        

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 relative z-10">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(47,107,255,0.2)', border: '1px solid rgba(47,107,255,0.4)' }}>
            
            <span className="text-xs font-semibold text-accent-light tracking-widest uppercase">
              MERCHANT APPLICATION
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight mb-5">
            Let&apos;s Connect &amp;{' '}
            <span style={{ color: '#6B9FFF' }}>Talk Payments</span>
          </h1>

          <p className="text-lg text-white/75 leading-relaxed max-w-2xl">
            You&apos;re one step away from experiencing better payment processing. Fill out the form below and a dedicated ChargeAct manager will reach out to discuss your firm and potential savings.
          </p>
        </div>
      </section>

      {/* ===== FORM SECTION ===== */}
      <section className="py-16 lg:py-20" aria-label="Application form">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
            {/* ---- Left: Form ---- */}
            <div className="lg:col-span-3">
              {submitted ?
              <div className="rounded-2xl p-10 text-center" style={{ background: '#F0FDF4', border: '1.5px solid #86EFAC' }}>
                  <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ background: '#22C55E' }}>
                  
                    <CheckIcon size={28} />
                  </div>
                  <h2 className="text-2xl font-extrabold text-brand-fg mb-3">Application Submitted!</h2>
                  <p className="text-brand-body text-base leading-relaxed mb-6">
                    Thank you! A dedicated ChargeAct manager will review your application and reach out within one business day to discuss your firm&apos;s payment processing needs.
                  </p>
                  <a
                  href="tel:+13237840656"
                  className="btn-primary-dark px-6 py-3 text-sm font-bold inline-flex items-center gap-2">
                  
                    <PhoneIcon size={14} /> Call Us Now: +1 (323) 784-0656
                  </a>
                </div> :

              <form onSubmit={handleSubmit} noValidate>
                  <div className="space-y-5">
                    {/* First + Last Name */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="first_name" className="form-label">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                        id="first_name"
                        name="first_name"
                        type="text"
                        required
                        placeholder="Jane"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="form-input" />
                      
                      </div>
                      <div>
                        <label htmlFor="last_name" className="form-label">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                        id="last_name"
                        name="last_name"
                        type="text"
                        required
                        placeholder="Smith"
                        value={formData.last_name}
                        onChange={handleChange}
                        className="form-input" />
                      
                      </div>
                    </div>

                    {/* Firm Name */}
                    <div>
                      <label htmlFor="firm_name" className="form-label">
                        Firm Name <span className="text-red-500">*</span>
                      </label>
                      <input
                      id="firm_name"
                      name="firm_name"
                      type="text"
                      required
                      placeholder="Smith & Associates CPA"
                      value={formData.firm_name}
                      onChange={handleChange}
                      className="form-input" />
                    
                    </div>

                    {/* Firm Website */}
                    <div>
                      <label htmlFor="website" className="form-label">Firm Website</label>
                      <input
                      id="website"
                      name="website"
                      type="url"
                      placeholder="https://yourfirm.com"
                      value={formData.website}
                      onChange={handleChange}
                      className="form-input" />
                    
                    </div>

                    {/* Industry + Volume */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="industry" className="form-label">
                          Industry <span className="text-red-500">*</span>
                        </label>
                        <select
                        id="industry"
                        name="industry"
                        required
                        value={formData.industry}
                        onChange={handleChange}
                        className="form-input">
                        
                          <option value="">Select your industry</option>
                          <option value="Accounting / CPA Firm">Accounting / CPA Firm</option>
                          <option value="Tax Preparation">Tax Preparation</option>
                          <option value="Bookkeeping Services">Bookkeeping Services</option>
                          <option value="Financial Advisory">Financial Advisory</option>
                          <option value="Payroll Services">Payroll Services</option>
                          <option value="Audit & Assurance">Audit &amp; Assurance</option>
                          <option value="Wealth Management">Wealth Management</option>
                          <option value="Professional Services">Professional Services</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="monthly_volume" className="form-label">
                          Monthly Processing Volume <span className="text-red-500">*</span>
                        </label>
                        <select
                        id="monthly_volume"
                        name="monthly_volume"
                        required
                        value={formData.monthly_volume}
                        onChange={handleChange}
                        className="form-input">
                        
                          <option value="">Select volume range</option>
                          <option value="Under $10,000/month">Under $10,000/month</option>
                          <option value="$10,000 – $50,000/month">$10,000 &ndash; $50,000/month</option>
                          <option value="$50,000 – $100,000/month">$50,000 &ndash; $100,000/month</option>
                          <option value="$100,000 – $500,000/month">$100,000 &ndash; $500,000/month</option>
                          <option value="Over $500,000/month">Over $500,000/month</option>
                        </select>
                      </div>
                    </div>

                    {/* Phone + Email */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="form-label">
                          Phone <span className="text-red-500">*</span>
                        </label>
                        <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        placeholder="(555) 000-0000"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input" />
                      
                      </div>
                      <div>
                        <label htmlFor="email" className="form-label">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="jane@yourfirm.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input" />
                      
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label htmlFor="notes" className="form-label">Additional Notes</label>
                      <textarea
                      id="notes"
                      name="notes"
                      rows={4}
                      placeholder="Tell us about your firm, current processor, or any specific needs..."
                      value={formData.notes}
                      onChange={handleChange}
                      className="form-input resize-none" />
                    
                    </div>

                    {error &&
                  <p className="text-red-500 text-sm font-medium">{error}</p>
                  }

                    {/* Submit */}
                    <button
                    type="submit"
                    disabled={!isValid || submitting}
                    className="w-full py-4 rounded-xl text-base font-bold transition-all duration-200 flex items-center justify-center gap-2"
                    style={{
                      background: isValid && !submitting ? '#2F6BFF' : '#9CA3AF',
                      color: '#ffffff',
                      cursor: isValid && !submitting ? 'pointer' : 'not-allowed',
                      boxShadow: isValid && !submitting ? '0 4px 14px rgba(47,107,255,0.35)' : 'none'
                    }}>
                    
                      {submitting ?
                    <>
                          <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                          </svg>
                          Submitting...
                        </> :

                    <>
                          Submit Application <ArrowIcon size={16} />
                        </>
                    }
                    </button>

                    <p className="text-xs text-brand-muted text-center leading-relaxed">
                      By submitting, you agree to our{' '}
                      <Link href="/terms" className="text-accent hover:underline">Terms of Service</Link>
                      {' '}and{' '}
                      <Link href="/privacy" className="text-accent hover:underline">Privacy Policy</Link>.
                    </p>
                  </div>
                </form>
              }
            </div>

            {/* ---- Right: Sidebar ---- */}
            <div className="lg:col-span-2 space-y-5">
              {/* What Happens Next */}
              <div className="rounded-2xl p-6" style={{ background: '#F9FAFB', border: '1.5px solid #E5E7EB' }}>
                <h3 className="font-extrabold text-brand-fg text-lg mb-5">What Happens Next</h3>
                <div className="space-y-4">
                  {[
                  {
                    num: '1',
                    title: 'Application Review',
                    desc: 'A ChargeAct manager reviews your application and firm details.'
                  },
                  {
                    num: '2',
                    title: 'Discovery Call',
                    desc: 'We reach out to discuss your firm, current setup, and potential savings.'
                  },
                  {
                    num: '3',
                    title: 'Custom Quote',
                    desc: 'You receive a transparent rate proposal tailored to your firm.'
                  },
                  {
                    num: '4',
                    title: 'Start Processing',
                    desc: "Once approved, you're set up and ready to accept payments."
                  }].
                  map((step) =>
                  <div key={step.num} className="flex gap-3">
                      <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-extrabold text-white"
                      style={{ background: '#2F6BFF' }}>
                      
                        {step.num}
                      </div>
                      <div>
                        <p className="font-bold text-brand-fg text-sm">{step.title}</p>
                        <p className="text-brand-body text-xs leading-relaxed mt-0.5">{step.desc}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Questions card */}
              <div
                className="rounded-2xl p-6"
                style={{ background: 'linear-gradient(135deg, #0A3D91 0%, #2F6BFF 100%)', border: '1.5px solid #2F6BFF' }}>
                
                <h3 className="font-extrabold text-white text-lg mb-3">Questions Before Applying?</h3>
                <p className="text-white/75 text-sm leading-relaxed mb-4">
                  Our team is happy to answer any questions about payment processing for CPA firms before you submit your application.
                </p>
                <a
                  href="tel:+13237840656"
                  className="inline-flex items-center gap-2 bg-white text-navy font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-blue-50 transition-colors">
                  
                  <PhoneIcon size={14} /> Call +1 (323) 784-0656
                </a>
              </div>

              {/* Why CPAs Choose ChargeAct */}
              <div className="rounded-2xl p-6" style={{ background: '#F9FAFB', border: '1.5px solid #E5E7EB' }}>
                <h3 className="font-extrabold text-brand-fg text-base mb-4">Why CPAs Choose ChargeAct</h3>
                <ul className="space-y-2.5">
                  {[
                  'Lower processing rates than Square & Stripe',
                  'Accept cards & ACH for every client invoice',
                  'Same-day & next-day funding available',
                  'Chargeback protection & dispute support',
                  'QuickBooks & Xero integration',
                  'Audit-ready payment records',
                  'No long-term contracts required'].
                  map((item) =>
                  <li key={item} className="flex items-start gap-2">
                      <span
                      className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-0.5"
                      style={{ background: '#EBF1FF' }}>
                      
                        <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                          <path d="M2 6l3 3 5-5" stroke="#2F6BFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span className="text-sm text-brand-body">{item}</span>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-brand-border py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img
                src="https://plumbers.joinchargeact.com/assets/images/catext_logo-1773689960451.png"
                alt="ChargeAct Logo"
                className="h-8 w-auto" />
            </div>
            <div className="text-xs text-brand-muted text-center">
&copy; 2026 ChargeAct. All Rights Reserved.
            </div>
            <div className="flex items-center gap-4 text-xs text-brand-muted">
              <Link href="/terms" className="hover:text-navy transition-colors">Terms &amp; Conditions</Link>
              <Link href="/privacy" className="hover:text-navy transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>);

}